import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Card, Input } from '../components/ui';
import { useToast } from '../components/ui/ToastProvider';
import {
  getCategories,
  createCategory,
  updateCategory,
  deleteCategory,
  createSubcategory,
  updateSubcategory,
  deleteSubcategory,
  Category,
  Subcategory
} from '../services/categoryService';

// Types are imported from categoryService

const Categories: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { addToast } = useToast();
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [isDeleteHasHover, setIsDeleteHasHover] = useState(false);

  // Modal states
  const [showAddCategoryModal, setShowAddCategoryModal] = useState(false);
  const [showEditCategoryModal, setShowEditCategoryModal] = useState(false);
  const [showAddSubcategoryModal, setShowAddSubcategoryModal] = useState(false);
  const [showEditSubcategoryModal, setShowEditSubcategoryModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  // Edit states
  const [editingCategory, setEditingCategory] = useState<Category | null>(null);
  const [editingSubcategory, setEditingSubcategory] = useState<{ subcategory: Subcategory; parentId: string } | null>(null);
  const [selectedParentId, setSelectedParentId] = useState<string>('');
  const [deleteTarget, setDeleteTarget] = useState<{ id: string; type: 'category' | 'subcategory'; parentId?: string } | null>(null);

  // Form states
  const [categoryForm, setCategoryForm] = useState({
    name: { en: '', ar: '' }
  });

  const [subcategoryForm, setSubcategoryForm] = useState({
    name: { en: '', ar: '' },
    parentId: ''
  });

  // Load categories from API
  useEffect(() => {
    const loadCategories = async () => {
      try {
        setLoading(true);
        const data = await getCategories();
        setCategories(data);
      } catch (error: any) {
        console.error('Failed to load categories:', error);
        addToast({
          title: t('notifications.error'),
          message: error?.message || t('notifications.operationFailed'),
          type: 'error'
        });
      } finally {
        setLoading(false);
      }
    };

    loadCategories();
  }, [addToast]);



  // Helper functions
  const resetCategoryForm = () => {
    setCategoryForm({
      name: { en: '', ar: '' }
    });
  };

  const resetSubcategoryForm = () => {
    setSubcategoryForm({
      name: { en: '', ar: '' },
      parentId: ''
    });
  };

  // Category CRUD operations
  const handleAddCategory = () => {
    resetCategoryForm();
    setShowAddCategoryModal(true);
  };

  const handleEditCategory = (category: Category) => {
    setEditingCategory(category);
    setCategoryForm({
      name: category.name
    });
    setShowEditCategoryModal(true);
  };

  const handleSaveCategory = async () => {
    if (!categoryForm.name.en || !categoryForm.name.ar) {
      addToast({ title: t('notifications.error'), message: t('forms.required'), type: 'error' });
      return;
    }

    try {
      if (editingCategory) {
        // Update existing category
        await updateCategory(editingCategory.id, categoryForm);
        addToast({ title: t('notifications.success'), message: t('categories.categoryUpdated'), type: 'success' });
        setShowEditCategoryModal(false);
      } else {
        // Add new category
        await createCategory(categoryForm);
        addToast({ title: t('notifications.success'), message: t('categories.categoryAdded'), type: 'success' });
        setShowAddCategoryModal(false);
      }

      // Refresh categories list
      const data = await getCategories();
      setCategories(data);

      resetCategoryForm();
      setEditingCategory(null);
    } catch (error: any) {
      console.error('Failed to save category:', error);
      addToast({
        title: t('notifications.error'),
        message: error?.message || t('notifications.operationFailed'),
        type: 'error'
      });
    }
  };

  const handleDeleteCategory = (id: string) => {
    setDeleteTarget({ id, type: 'category' });
    setShowDeleteModal(true);
  };

  const handleDeleteSubcategory = (categoryId: string, subcategoryId: string) => {
    setDeleteTarget({ id: subcategoryId, type: 'subcategory', parentId: categoryId });
    setShowDeleteModal(true);
  };

  // Subcategory CRUD operations
  const handleAddSubcategory = (parentId: string) => {
    resetSubcategoryForm();
    setSelectedParentId(parentId);
    setSubcategoryForm(prev => ({ ...prev, parentId }));
    setShowAddSubcategoryModal(true);
  };

  const handleEditSubcategory = (subcategory: Subcategory, parentId: string) => {
    setEditingSubcategory({ subcategory, parentId });
    setSubcategoryForm({
      name: subcategory.name,
      parentId
    });
    setShowEditSubcategoryModal(true);
  };

  const handleSaveSubcategory = async () => {
    if (!subcategoryForm.name.en || !subcategoryForm.name.ar) {
      addToast({ title: t('notifications.error'), message: t('forms.required'), type: 'error' });
      return;
    }

    if (!subcategoryForm.parentId) {
      addToast({ title: t('notifications.error'), message: t('forms.required'), type: 'error' });
      return;
    }

    try {
      if (editingSubcategory) {
        // Update existing subcategory
        await updateSubcategory(editingSubcategory.subcategory.id, { name: subcategoryForm.name });
        addToast({ title: t('notifications.success'), message: t('categories.subcategoryUpdated'), type: 'success' });
        setShowEditSubcategoryModal(false);
      } else {
        // Add new subcategory
        await createSubcategory({
          name: subcategoryForm.name,
          parentId: subcategoryForm.parentId
        });
        addToast({ title: t('notifications.success'), message: t('categories.subcategoryAdded'), type: 'success' });
        setShowAddSubcategoryModal(false);
      }

      // Refresh categories list
      const data = await getCategories();
      setCategories(data);

      resetSubcategoryForm();
      setEditingSubcategory(null);
      setSelectedParentId('');
    } catch (error: any) {
      console.error('Failed to save subcategory:', error);
      addToast({
        title: t('notifications.error'),
        message: error?.message || t('notifications.operationFailed'),
        type: 'error'
      });
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;

    try {
      if (deleteTarget.type === 'category') {
        await deleteCategory(deleteTarget.id);
        addToast({ title: t('notifications.success'), message: t('categories.categoryDeleted'), type: 'success' });
      } else {
        await deleteSubcategory(deleteTarget.id);
        addToast({ title: t('notifications.success'), message: t('categories.subcategoryDeleted'), type: 'success' });
      }

      // Refresh categories list
      const data = await getCategories();
      setCategories(data);

      setShowDeleteModal(false);
      setDeleteTarget(null);
    } catch (error: any) {
      console.error('Failed to delete:', error);
      addToast({
        title: t('notifications.error'),
        message: error?.message || t('notifications.operationFailed'),
        type: 'error'
      });
    }
  };

  const cancelDelete = () => {
    setShowDeleteModal(false);
    setDeleteTarget(null);
  };



  if (loading) {
    return (
      <div className="page-container">
        <div className="page-header">
          <h1 className="page-title">{t('navigation.categories')}</h1>
        </div>
        <div className="page-content">
          <div className="loading-spinner">
            <div className="spinner"></div>
            <p>{t('common.loading')}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <div className="page-header">
        <h1 className="page-title">{t('navigation.categories')}</h1>
        <div className="page-actions">
          <Button
            variant="primary"
            onClick={handleAddCategory}
          >
            {t('categories.addCategory')}
          </Button>
        </div>
      </div>

      <div className="page-content">


        <div className="categories-grid">
          {categories.map(category => (
            <Card key={category.id} className="category-card enhanced">
              <div className="category-header">
                <div className="category-title-section">
                  <div className="category-title-row">
                    <h3 className="category-name">
                      {i18n.language === 'en' ? category.name.en : category.name.ar}
                    </h3>
                    <div className="category-header-actions">
                      <button
                        className="action-btn edit"
                        onClick={() => handleEditCategory(category)}
                        title={t('common.edit')}
                      >
                        ✏️
                      </button>
                      <button
                        className="action-btn delete"
                        onClick={() => handleDeleteCategory(category.id)}
                        title={t('common.delete')}
                      >
                        🗑️
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              <div className="subcategories-section">
                <div className="subcategories-header">
                  <h4 className="subcategories-title">{t('categories.subcategories')} ({category.subcategories.length})</h4>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleAddSubcategory(category.id)}
                    className="add-subcategory-btn"
                  >
                    {t('categories.addSubcategory')}
                  </Button>
                </div>

                {category.subcategories.length > 0 ? (
                  <div className="subcategories-list">
                    {category.subcategories.map(subcategory => (
                      <div key={subcategory.id} className="subcategory-item">
                        <div className="subcategory-info">
                          <div className="subcategory-header">
                            <h5 className="subcategory-name">
                              {i18n.language === 'en' ? subcategory.name.en : subcategory.name.ar}
                            </h5>
                          </div>

                        </div>
                        <div className="subcategory-actions">
                          <button
                            className="action-btn edit"
                            onClick={() => handleEditSubcategory(subcategory, category.id)}
                            title={t('common.edit')}
                          >
                            ✏️
                          </button>
                          <button
                            className="action-btn delete"
                            onClick={() => handleDeleteSubcategory(category.id, subcategory.id)}
                            title={t('common.delete')}
                          >
                            🗑️
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="empty-subcategories">
                    <p>{t('categories.noSubcategories')}</p>
                  </div>
                )}
              </div>


            </Card>
          ))}
        </div>

        {categories.length === 0 && (
          <div className="empty-state">
            <p>{t('categories.noCategories')}</p>
          </div>
        )}
      </div>

      {/* Add Category Modal */}
      {showAddCategoryModal && (
        <div className="modal-overlay" onClick={() => setShowAddCategoryModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{t('categories.addNewCategory')}</h2>
              <button className="modal-close" onClick={() => setShowAddCategoryModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <div className="form-row">
                <div className="form-group">
                  <label>{t('categories.categoryNameEn')}</label>
                  <Input
                    value={categoryForm.name.en}
                    onChange={(e) => setCategoryForm(prev => ({
                      ...prev,
                      name: { ...prev.name, en: e.target.value }
                    }))}
                    placeholder={t('categories.enterCategoryNameEn')}
                  />
                </div>
                <div className="form-group">
                  <label>{t('categories.categoryNameAr')}</label>
                  <Input
                    value={categoryForm.name.ar}
                    onChange={(e) => setCategoryForm(prev => ({
                      ...prev,
                      name: { ...prev.name, ar: e.target.value }
                    }))}
                    placeholder={t('categories.enterCategoryNameAr')}
                  />
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <Button variant="secondary" onClick={() => setShowAddCategoryModal(false)}>
                {t('common.cancel')}
              </Button>
              <Button variant="primary" onClick={handleSaveCategory}>
                {t('categories.addCategory')}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Category Modal */}
      {showEditCategoryModal && (
        <div className="modal-overlay" onClick={() => setShowEditCategoryModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{t('categories.editCategoryTitle')}</h2>
              <button className="modal-close" onClick={() => setShowEditCategoryModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <div className="form-row">
                <div className="form-group">
                  <label>{t('categories.categoryNameEn')}</label>
                  <Input
                    value={categoryForm.name.en}
                    onChange={(e) => setCategoryForm(prev => ({
                      ...prev,
                      name: { ...prev.name, en: e.target.value }
                    }))}
                    placeholder={t('categories.enterCategoryNameEn')}
                  />
                </div>
                <div className="form-group">
                  <label>{t('categories.categoryNameAr')}</label>
                  <Input
                    value={categoryForm.name.ar}
                    onChange={(e) => setCategoryForm(prev => ({
                      ...prev,
                      name: { ...prev.name, ar: e.target.value }
                    }))}
                    placeholder={t('categories.enterCategoryNameAr')}
                  />
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <Button variant="secondary" onClick={() => setShowEditCategoryModal(false)}>
                {t('common.cancel')}
              </Button>
              <Button variant="primary" onClick={handleSaveCategory}>
                {t('categories.updateCategory')}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Add Subcategory Modal */}
      {showAddSubcategoryModal && (
        <div className="modal-overlay" onClick={() => setShowAddSubcategoryModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{t('categories.addNewSubcategory')}</h2>
              <button className="modal-close" onClick={() => setShowAddSubcategoryModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <div className="form-row">
                <div className="form-group">
                  <label>{t('categories.subcategoryNameEn')}</label>
                  <Input
                    value={subcategoryForm.name.en}
                    onChange={(e) => setSubcategoryForm(prev => ({
                      ...prev,
                      name: { ...prev.name, en: e.target.value }
                    }))}
                    placeholder={t('categories.enterSubcategoryNameEn')}
                  />
                </div>
                <div className="form-group">
                  <label>{t('categories.subcategoryNameAr')}</label>
                  <Input
                    value={subcategoryForm.name.ar}
                    onChange={(e) => setSubcategoryForm(prev => ({
                      ...prev,
                      name: { ...prev.name, ar: e.target.value }
                    }))}
                    placeholder={t('categories.enterSubcategoryNameAr')}
                  />
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <Button variant="secondary" onClick={() => setShowAddSubcategoryModal(false)}>
                {t('common.cancel')}
              </Button>
              <Button variant="primary" onClick={handleSaveSubcategory}>
                {t('categories.addSubcategory')}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Subcategory Modal */}
      {showEditSubcategoryModal && (
        <div className="modal-overlay" onClick={() => setShowEditSubcategoryModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{t('categories.editSubcategoryTitle')}</h2>
              <button className="modal-close" onClick={() => setShowEditSubcategoryModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <div className="form-row">
                <div className="form-group">
                  <label>{t('categories.subcategoryNameEn')}</label>
                  <Input
                    value={subcategoryForm.name.en}
                    onChange={(e) => setSubcategoryForm(prev => ({
                      ...prev,
                      name: { ...prev.name, en: e.target.value }
                    }))}
                    placeholder={t('categories.enterSubcategoryNameEn')}
                  />
                </div>
                <div className="form-group">
                  <label>{t('categories.subcategoryNameAr')}</label>
                  <Input
                    value={subcategoryForm.name.ar}
                    onChange={(e) => setSubcategoryForm(prev => ({
                      ...prev,
                      name: { ...prev.name, ar: e.target.value }
                    }))}
                    placeholder={t('categories.enterSubcategoryNameAr')}
                  />
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <Button variant="secondary" onClick={() => setShowEditSubcategoryModal(false)}>
                {t('common.cancel')}
              </Button>
              <Button variant="primary" onClick={handleSaveSubcategory}>
                {t('categories.updateSubcategory')}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="modal-overlay" onClick={cancelDelete}>
          <div className="modal-content delete-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{t('categories.confirmDeleteTitle')}</h2>
              <button className="modal-close" onClick={cancelDelete}>×</button>
            </div>
            <div className="modal-body">
              <div className="delete-confirmation">
                <div className="delete-icon">🗑️</div>
                <h3>{t('categories.deleteConfirm', { type: deleteTarget?.type === 'category' ? t('categories.categoryName').toLowerCase() : t('categories.subcategoryName').toLowerCase() })}</h3>
                <p>{t('categories.deleteMessage', { type: deleteTarget?.type === 'category' ? t('categories.categoryName').toLowerCase() : t('categories.subcategoryName').toLowerCase() })}</p>
              </div>
            </div>
            <div className="modal-footer">
              <Button variant="secondary" onClick={cancelDelete}>
                {t('common.cancel')}
              </Button>
              <Button style={{
                color: isDeleteHasHover ? 'red' : 'white',
                background: !isDeleteHasHover ? 'red' : 'white',
              }}
              onMouseEnter={() => {
                setIsDeleteHasHover(true);
              }}
              onMouseLeave={() => {
                setIsDeleteHasHover(false);
              }}
              variant="error" onClick={confirmDelete}>
                {deleteTarget?.type === 'category' ? t('categories.deleteCategory') : t('categories.deleteSubcategory')}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Categories;